<template>
  <div class="bed-visualizer-image">
    <div class="bed-visualizer-image__group bed-visualizer-image__group--base">
      <div
        class="bed-visualizer-image__layer"
        :style="{
          zIndex: baseStackOrder
        }"
      >
        <responsive-image
          v-if="baseImage"
          :class="$blockClass(blockClass, 'bed-visualizer-image')"
          :image="baseImage"
          :lazy-load="false"
          block-class="bed-visualizer-image"
          min-max="320-1440"
          ratio="1:1"
          sizes="1440px"
        />
      </div>
    </div>

    <div class="bed-visualizer-image__group bed-visualizer-image__group--base-pillow">
      <div
        class="bed-visualizer-image__layer"
        :style="{
          zIndex: basePillowStackOrder
        }"
      >
        <responsive-image
          v-if="basePillowImage"
          :class="$blockClass(blockClass, 'bed-visualizer-image')"
          :image="basePillowImage"
          :lazy-load="false"
          block-class="bed-visualizer-image"
          min-max="320-1440"
          ratio="1:1"
          sizes="1440px"
        />
      </div>
    </div>

    <div class="bed-visualizer-image__group bed-visualizer-image__group--base-sheet">
      <div
        class="bed-visualizer-image__layer"
        :style="{
          zIndex: baseSheetStackOrder
        }"
      >
        <responsive-image
          v-if="baseSheetImage"
          :class="$blockClass(blockClass, 'bed-visualizer-image')"
          :image="baseSheetImage"
          :lazy-load="false"
          block-class="bed-visualizer-image"
          min-max="320-1440"
          ratio="1:1"
          sizes="1440px"
        />
      </div>
    </div>

    <div
      v-for="group in transformedData"
      :key="group.name"
      :class="[`bed-visualizer-image__group--${group.handle}`]"
      class="bed-visualizer-image__group"
    >
      <template
        v-for="variant in group.options"
        :key="variant.id"
      >
        <div
          v-for="(image, index) in variant?.images?.visualizer"
          :key="`${variant.id}${index}`"
          class="bed-visualizer-image__layer"
          :class="[`bed-visualizer-image__layer--${index}`]"
          :style="{
            zIndex: variant.stackOrders ? (variant.stackOrders[index] || 1) : 1,
          }"
        >
          <responsive-image
            v-if="image"
            :class="$blockClass(blockClass, 'bed-visualizer-image')"
            :image="image"
            :lazy-load="false"
            block-class="bed-visualizer-image"
            min-max="320-1440"
            ratio="1:1"
            sizes="1440px"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Bed visualizer image (bed-visualizer-image)
 * -----------------------------------------------------------------------------
 * Displays contents of bed visualizer image.
 *
 * @param {Object} baseImage - Base visualizer image.
 * @param {Object} baseSheetImage - Base visualizer pillow image.
 * @param {Object} basePillowImage - Base visualizer sheet image.
 * @param {Number} baseStackOrder - Base image stack order.
 * @param {Number} baseSheetStackOrder - Base sheet stack order.
 * @param {Number} basePillowStackOrder - Base pillow stack order.
 *
 */
import { mapState } from 'vuex'

import ResponsiveImage from '~global/images/responsive-image'
import { convertToHandle } from '~/helpers/convert'

export default {
  name: 'BedVisualizerImage',

  components: {
    ResponsiveImage,
  },

  props: {
    baseImage: {
      type: Object,
      default: () => ({}),
    },
    blockClass: {
      type: String,
      default: '',
    },
    basePillowImage: {
      type: Object,
      default: () => ({}),
    },
    baseSheetImage: {
      type: Object,
      default: () => ({}),
    },
    baseStackOrder: {
      type: Number,
      default: 1,
    },
    basePillowStackOrder: {
      type: Number,
      default: 3,
    },
    baseSheetStackOrder: {
      type: Number,
      default: 5,
    },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      selectedOptions: (state) => state['bed-visualizer'].config?.selectedOptions,
    }),

    /**
     * Transform selectedOptions into array for easier looping in Vue templates.
     * @returns {Array}
     */
    transformedData() {
      if (!this.selectedOptions) {
        return
      }

      return Object.keys(this.selectedOptions).map((key) => {
        return {
          handle: convertToHandle(key),
          name: key,
          options: this.selectedOptions[key],
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import './bed-visualizer-image';
</style>
